import React, { useState, useEffect, useMemo } from "react";
import { getAllCourses } from "../../../../services/course.service";
import EventPlanner from "./EventPlanner";
import HeroStatsBar from "../../../StudentDashboard/Components/ui/HeroStatsBar";
import { DashboardHeroSkeleton } from "../../../StudentDashboard/Components/ui/Skeleton";
import {
  pageTitle,
  bodySecondary,
  accentBarSection,
  pageBg,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

const TeacherDashboard2 = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const data = await getAllCourses();
        setDashboardData(data);
      } catch (err) {
        setError("Failed to load dashboard data. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // --- STATS CALCULATIONS ---

  const totalStudentsCount = useMemo(() => {
    if (!dashboardData?.courses) return 0;
    const studentIds = new Set();
    dashboardData.courses.forEach((course) => {
      if (course.attendance) {
        const sessions = Array.isArray(course.attendance.sessions)
          ? course.attendance.sessions
          : [];
        sessions.forEach((session) => {
          (session.attendees || []).forEach((id) => studentIds.add(id));
        });
      }
    });
    return studentIds.size;
  }, [dashboardData]);

  const pendingGradesCount = useMemo(() => {
    if (!dashboardData?.courses) return 0;
    let pendingCount = 0;
    dashboardData.courses.forEach((course) => {
      course.assignments?.forEach((assignment) => {
        const ungradedSubmissions =
          assignment.submissions?.filter((sub) => !sub.isGraded).length || 0;
        pendingCount += ungradedSubmissions;
      });
    });
    return pendingCount;
  }, [dashboardData]);

  const upcomingLecturesCount = useMemo(() => {
    if (!dashboardData?.courses) return 0;
    return dashboardData.courses.reduce(
      (sum, course) => sum + (course.meetingsNext7DaysCount || 0),
      0
    );
  }, [dashboardData?.courses]);

  // Loading state — skeleton instead of spinner
  if (loading) {
    return (
      <div className={cn(pageBg, "px-4 md:px-8 lg:px-[15vh] py-6 space-y-6")}>
        <div>
          <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-sm h-8 w-64 mb-2" />
          <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-sm h-4 w-80 mb-3" />
          <div className="h-1 w-12 bg-gray-200 dark:bg-gray-700 rounded-sm" />
        </div>
        <DashboardHeroSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(pageBg, "flex justify-center items-center h-screen w-full")}>
        <p className="text-xl text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const totalCourses = dashboardData?.user?.totalCourses || 0;
  const totalStudents = dashboardData?.user?.totalStudents || 0;

  const stats = [
    { id: "courses", label: "Active Courses", value: totalCourses },
    { id: "students", label: "Total Students", value: totalStudents },
    { id: "pending", label: "Pending Grades", value: pendingGradesCount },
    { id: "lectures", label: "Upcoming Lectures", value: upcomingLecturesCount },
  ];

  return (
    <div className="space-y-4 md:space-y-6 px-4 md:px-8 lg:px-[15vh] py-2 mx-auto w-full bg-white dark:bg-gray-900 overflow-y-auto h-full mb-30">
      {/* Welcome Header */}
      <div>
        <h1 className={pageTitle}>
          Welcome back, {dashboardData.user.name}!
        </h1>
        <p className={cn(bodySecondary, "mt-1")}>
          Here's what's happening in your classes today.
        </p>
        <div className={cn(accentBarSection, "mt-3")} />
      </div>

      {/* Stats Cards */}
      <HeroStatsBar stats={stats} />

      {/* Event Planner Calendar */}
      <EventPlanner courses={dashboardData.courses} />
    </div>
  );
};

export default TeacherDashboard2;
